class CartDrawer extends HTMLElement {
    constructor() {
        super();

        this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
        this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
        this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector("#cart-icon-bubble");
        cartLink.setAttribute("role", "button");
        cartLink.setAttribute("aria-haspopup", "dialog");
        cartLink.addEventListener("click", (event) => {
            event.preventDefault();
            this.open(cartLink);
        });
        cartLink.addEventListener("keydown", (event) => {
            if (event.code.toUpperCase() === "SPACE") {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    open(triggeredBy) {
        if (typeof triggeredBy === 'element') this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
            this.classList.add("animate", "active");
        });

        this.addEventListener(
            "transitionend",
            () => {
                const containerToTrapFocusOn = this.classList.contains("is-empty") ? this.querySelector(".drawer__inner-empty") : document.getElementById("CartDrawer");
                const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
                trapFocus(containerToTrapFocusOn, { focusElement });

                // Signal that the drawer is open
                this.dispatchEvent(new CustomEvent("drawer:open", {
                    detail: {
                        triggeredBy,
                    }
                }));
            },
            { once: true }
        );

        document.body.classList.add("overflow-hidden");
    }

    close() {
        this.classList.remove("active");
        removeTrapFocus(this.activeElement);
        document.body.classList.remove("overflow-hidden");

        this.addEventListener(
            "transitionend",
            () => {
                this.dispatchEvent(new CustomEvent("drawer:close"));
            },
            { once: true }
        );
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute("role", "button");
        cartDrawerNote.setAttribute("aria-expanded", "false");

        if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
            cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
        }

        cartDrawerNote.addEventListener("click", (event) => {
            event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
        });

        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
    }

    renderContents(parsedState, renderedBy) {
        this.querySelector(".drawer__inner").classList.contains("is-empty") && this.querySelector(".drawer__inner").classList.remove("is-empty");
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        setTimeout(() => {
            this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
            this.open(renderedBy);
        });
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
        return [
            {
                id: "cart-drawer",
                selector: "#CartDrawer"
            },
            {
                id: "cart-icon-bubble"
            }
        ];
    }

    getSectionDOM(html, selector = ".shopify-section") {
        return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
    constructor() {
        super();
        this.renderRelatedItems();
    }

    connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            this.renderRelatedItems();
            if (event.source === "cart-items") {
                return;
            }
            this.onCartUpdate();
        });
    }

    renderRelatedItems() {
        if (this.querySelector(".js-item-id")) {
            this.firstProductId = this.querySelector(".js-item-id").value;
        }
        if (this.firstProductId) {
            fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${this.firstProductId}&limit=3&intent=related`)
                .then((response) => response.json())
                .then(({ products }) => {
                    if (products.length > 0) {
                        let listHTML = "";
                        products.forEach((product) => {
                            let imgHTML = `<div class="related-products-card__media related-products-card__media--no-media"></div>`;
                            if (product.featured_image) {
                                imgHTML = `<div class="related-products-card__media">
                              <img
                                src="${product.featured_image}"
                                alt=""
                                loading="lazy"
                                width="200"
                                height="200"
                              >
                            </div>`;
                            }
                            const onSale = product.compare_at_price > product.price;
                            const itemClasses = `related-products-card__item ${product.available ? "" : "related-products-card--unavailable"}  ${onSale ? "" : "related-products-card--on-sale"}`;
                            let priceHTML = `<div class="price"><div class="price__regular"><span class="price-item price-item--regular">${this.formatMoney(product.price)}</span></div></div>`;
                            if (onSale) {
                                priceHTML = `<div class="price price--on-sale">
                                <div class="price__sale">
                                    <span class="visually-hidden visually-hidden--inline">Sale price</span>
                                    <span class="price-item price-item--sale">${this.formatMoney(product.price)}</span>
                                    <span class="visually-hidden visually-hidden--inline">Regular price</span>
                                    <span>
                                        <s class="price-item price-item--regular price-item--last">${this.formatMoney(product.compare_at_price)}</s>
                                    </span>
                                </div>`;
                            }
                            listHTML += `<li class="${itemClasses}">
                              <div class="related-products-card">
                                ${imgHTML}
                                <div class="related-products-card__content">
                                  <div class="related-products-card__vendor">${product.vendor}</div>
                                  <h3 class="related-products-card__heading">
                                    <a href="${product.url}" class="related-products-card__link">${product.title}</a>
                                  </h3>
                                  <div class="related-products-card__price">${priceHTML}</div>
                                </div>
                              </div>
                            </li>`;
                        });

                        document.querySelector("#js-related-product-drawer").classList.remove("is-hidden");
                        document.querySelector("#js-related-product-drawer-list").innerHTML = listHTML;
                    }
                });
        } else {
            document.querySelector("#js-related-product-drawer").classList.add("is-hidden");
            document.querySelector("#js-related-product-drawer-list").innerHTML = "";
        }
    }

    formatMoney(cents) {
        if (typeof cents == "string") {
            cents = cents.replace(".", "");
        }
        var value = "";
        var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        var formatString = "${{amount}}";

        function defaultOption(opt, def) {
            return typeof opt == "undefined" ? def : opt;
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
            precision = defaultOption(precision, 2);
            thousands = defaultOption(thousands, ",");
            decimal = defaultOption(decimal, ".");

            if (isNaN(number) || number == null) {
                return 0;
            }

            number = (number / 100.0).toFixed(precision);

            var parts = number.split("."),
                dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
                cents = parts[1] ? decimal + parts[1] : "";

            return dollars + cents;
        }

        switch (formatString.match(placeholderRegex)[1]) {
            case "amount":
                value = formatWithDelimiters(cents, 2);
                break;
            case "amount_no_decimals":
                value = formatWithDelimiters(cents, 0);
                break;
            case "amount_with_comma_separator":
                value = formatWithDelimiters(cents, 2, ".", ",");
                break;
            case "amount_no_decimals_with_comma_separator":
                value = formatWithDelimiters(cents, 0, ".", ",");
                break;
        }

        return formatString.replace(placeholderRegex, value);
    }

    getSectionsToRender() {
        return [
            {
                id: "CartDrawer",
                section: "cart-drawer",
                selector: ".drawer__inner"
            },
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
                selector: ".shopify-section"
            }
        ];
    }
}

customElements.define("cart-drawer-items", CartDrawerItems);
